"use strict";

import React from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router';
import Config from '../lib/Config';
import Router from '../lib/Router';
import AppStore from '../stores/AppStore';
import MigrationStore from '../stores/MigrationStore';
import NotificationActions from '../actions/NotificationActions';
import AnalyticsActions from '../actions/AnalyticsActions';
import MigrationActions from '../actions/MigrationActions';
import Button from './ui/Button.react';
import Spinner from './ui/Spinner2.react';
import AccountMigrationBrandHeader from './AccountMigrationBrandHeader.react';
import MigrationComplete from './MigrationComplete.react';
import MigrationInProgress from './MigrationInProgress.react';
import MigrationMultiFailed from './MigrationMultiFailed.react';
import {parseErrorMessage} from '../lib/ErrorParser';
import {
    canRequestMigration,
    getMigrationErrorCode,
    getMigrationPageView,
    MigrationStatusValue,
    MigrationPageView
} from '../lib/migrationUtils';

/**
 * Signed-in account migration flow (Virgin Media Store → Rakuten TV).
 */
export default class AccountMigrationPage extends React.Component {
    state = {
        termsAccepted: false
    };

    /** True between submitting a migrate request and its result arriving. */
    _awaitingMigrationResult = false;
    /**
     * True once this visit has submitted a migrate request that did not fail.
     * Those users wait on a spinner rather than the Web 10 in-progress copy,
     * which is only for arriving at /migrate with migration already running.
     */
    _migrateRequestedThisVisit = false;
    _redirectTimeout = null;

    static contextTypes = {
        router: PropTypes.object
    };

    componentDidMount() {
        document.body.className += " black-background";
        MigrationStore.addUpdateListener(this._onMigrationStoreUpdate);
        MigrationActions.getMigrationStatus();
    }

    componentWillUnmount() {
        document.body.className = document.body.className.replace(/black-background/, '');
        MigrationStore.removeUpdateListener(this._onMigrationStoreUpdate);
        if (this._redirectTimeout) {
            clearTimeout(this._redirectTimeout);
            this._redirectTimeout = null;
        }
    }

    _onMigrationStoreUpdate = () => {
        const {migrationStatus, statusLoading, migrationSubmitting, error, redirectUrl} = MigrationStore;

        if (!statusLoading && migrationStatus) {
            const termsAccepted = !!migrationStatus.termsAcceptedDateTime;
            if (termsAccepted !== this.state.termsAccepted) {
                this.setState({termsAccepted});
            }
        }

        if (!migrationSubmitting && !statusLoading && this._awaitingMigrationResult) {
            this._awaitingMigrationResult = false;
            if (error) {
                this._migrateRequestedThisVisit = false;
                const detail = this._getErrorDetail();
                // Flux forbids nested dispatches; defer to the next tick so the
                // current REQUEST_MIGRATION_* dispatch can finish first.
                setTimeout(() => {
                    NotificationActions.displayNotification({
                        notificationType: 'error',
                        content: detail
                            ? AppStore.translate('view.account_migration_page.migration_message.error_with_detail', {detail})
                            : AppStore.translate('view.account_migration_page.migration_message.error')
                    });
                }, 0);
            } else if (redirectUrl) {
                if (!this._redirectTimeout) {
                    // Leave the spinner on screen for a beat, then hand off to
                    // Rakuten TV.
                    this._redirectTimeout = setTimeout(() => {
                        window.location.assign(redirectUrl);
                    }, 4750);
                }
            } else {
                // No hand-off URL means the request never reached submission: the
                // status had already moved on, so show the matching outcome view.
                this._migrateRequestedThisVisit = false;
            }
        }

        // render() reads migrationSubmitting / statusLoading / migrationStatus directly
        // from the store, so we need to re-render on every store change, not just when
        // termsAccepted differs.
        this.forceUpdate();
    };

    _renderLoading = () => (
        <div className="account-migration__loading" aria-busy="true">
            <Spinner />
        </div>
    );

    _renderMigrationOutcome = (pageView, waitingOnMigrateClick) => {
        switch (pageView) {
            case MigrationPageView.COMPLETE:
                return <MigrationComplete />;
            case MigrationPageView.IN_PROGRESS:
                return <MigrationInProgress loading={waitingOnMigrateClick} />;
            case MigrationPageView.MULTI_FAILED:
                return <MigrationMultiFailed />;
            default:
                return null;
        }
    };

    /**
     * Whatever the API said about the failure, when it is worth showing: a
     * mapped subcode, or the plain `Message` from the response body (see
     * APIRequestError). Unmapped subcodes parse back to the key itself, which is
     * not showable.
     *
     * @return {?string}
     */
    _getErrorDetail = () => {
        const error = MigrationStore.error;
        const message = error && error.message ? String(error.message).trim() : '';
        if (!message) {
            return null;
        }

        let detail = message;
        if (getMigrationErrorCode(error)) {
            detail = parseErrorMessage(message);
            if (detail.indexOf('error_code_') === 0) {
                return null;
            }
        }

        // More copy follows the detail, so it has to close as a sentence.
        return /[.!?]$/.test(detail) ? detail : `${detail}.`;
    };

    /**
     * Web 4: the failure sitting above the form. The error code and message are
     * only around for the request that just failed, so a reload falls back to
     * the plain message.
     */
    _renderFailureMessage = () => {
        const errorCode = getMigrationErrorCode(MigrationStore.error);
        const detail = this._getErrorDetail();

        return (
            <div className="account-migration__single-failure-small">
                {errorCode
                    ? (
                        <p className="account-migration__error-code">
                            {AppStore.translate('view.account_migration_page.error_code_label', {errorCode})}
                        </p>
                    )
                    : null}
                {errorCode && detail
                    ? <p className="account-migration__error-detail">{detail}</p>
                    : null}
                <p>
                    {errorCode
                        ? AppStore.translate('view.account_migration_page.migration_message.error_with_code')
                        : (detail
                            ? AppStore.translate('view.account_migration_page.migration_message.error_with_detail', {detail})
                            : AppStore.translate('view.account_migration_page.migration_message.error'))}
                </p>
            </div>
        );
    };

    _renderMigrationForm = (isSubmitting, canMigrate, status, statusLoading) => (
        <>
            {/* A rejected request leaves the stored status untouched, so the error
                itself has to bring the message up as well as a FAILED status. */}
            {(status === MigrationStatusValue.FAILED || !!MigrationStore.error) && !isSubmitting
                ? this._renderFailureMessage()
                : null}
            <h1 className="account-migration__headline">
                {AppStore.translate('view.account_migration_page.headline')}
            </h1>
            <div className="account-migration__details">
                <ul
                    className="account-migration__benefits"
                    aria-label={AppStore.translate('view.account_migration_page.benefits_label')}
                >
                    <li className="account-migration__benefit">
                        {AppStore.translate('view.account_migration_page.benefit_1')}
                    </li>
                    <li className="account-migration__benefit">
                        {AppStore.translate('view.account_migration_page.benefit_2')}
                    </li>
                    <li className="account-migration__benefit">
                        {AppStore.translate('view.account_migration_page.benefit_3')}
                    </li>
                    <li className="account-migration__benefit">
                        {AppStore.translate('view.account_migration_page.benefit_4')}
                    </li>
                </ul>
                <p className="account-migration__confirm-heading">
                    {AppStore.translate('view.account_migration_page.confirm_heading')}
                </p>
                <label className="account-migration__accept">
                    <input
                        type="checkbox"
                        className="account-migration__checkbox"
                        checked={this.state.termsAccepted}
                        disabled={isSubmitting || !canMigrate || statusLoading}
                        onChange={this._onTermsChange}
                    />
                    <span className="account-migration__accept-text">
                        {AppStore.translate('view.account_migration_page.accept_label')}
                        <Link
                            className="account-migration__inline-link"
                            to={Router.MIGRATION_TERMS_AND_CONDITIONS}
                            onClick={this._trackTermsLink}
                        >
                            {AppStore.translate('view.account_migration_page.terms_link')}
                        </Link>
                    </span>
                </label>
            </div>
            <Button
                type="button"
                shape="square"
                className="cta-play full-width"
                disabled={!this.state.termsAccepted || isSubmitting || !canMigrate || statusLoading}
                onClick={this._onMigrate}
                updatePending={isSubmitting}
            >
                {isSubmitting
                    ? AppStore.translate('view.account_migration_page.migrating')
                    : AppStore.translate('view.account_migration_page.migrate_cta')}
            </Button>
        </>
    );

    render() {
        const {statusLoading, migrationSubmitting, migrationStatus} = MigrationStore;
        const status = migrationStatus ? migrationStatus.migrationStatus : null;
        const failCount = migrationStatus ? migrationStatus.failCount : 0;
        let pageView = status ? getMigrationPageView(status, failCount) : MigrationPageView.FORM;
       
        const showOutcome = !statusLoading && pageView !== MigrationPageView.FORM;
        const canMigrate = !status || canRequestMigration(status);
        const isSubmitting = migrationSubmitting;
        // Users who pressed Transfer wait on a spinner until the Rakuten hand-off;
        // only those arriving at /migrate see the Web 10 copy. The outcome views
        // take the flag themselves, so a status change still swaps the content.
        const waitingOnMigrateClick = isSubmitting || this._migrateRequestedThisVisit;

        return (
            <main className="account-page account-page--sign-in account-page--migration">
                <AccountMigrationBrandHeader useCompositeImage={true} />                
                <div className="account-migration">
                    {statusLoading
                        ? this._renderLoading()
                        : (showOutcome
                            ? this._renderMigrationOutcome(pageView, waitingOnMigrateClick)
                            : (waitingOnMigrateClick
                                ? this._renderLoading()
                                : this._renderMigrationForm(isSubmitting, canMigrate, status, statusLoading)))}
                </div>
            </main>
        );
    }

    _onTermsChange = (event) => {
        this.setState({termsAccepted: event.target.checked});
    };

    _trackTermsLink = () => {
        AnalyticsActions.trackEvent({
            category: 'Account Migration',
            action: 'Terms',
            label: 'Open terms'
        });
    };

    _onMigrate = () => {
        if (!this.state.termsAccepted) {
            NotificationActions.displayNotification({
                notificationType: 'error',
                content: AppStore.translate('view.account_migration_page.error_accept')
            });
            return;
        }
        this._awaitingMigrationResult = true;
        this._migrateRequestedThisVisit = true;
        AnalyticsActions.trackEvent({
            category: 'Account Migration',
            action: 'Migrate',
            label: 'Migrate Now'
        });
        MigrationActions.requestMigration();
    };
}
